import { PermissionRequirementPair } from '@vyce/core/src/api/types';
import { AppNotification } from '@vyce/core/src/types';

// The 409 body lists the pairs that block a removal, e.g.
// "time:can_create_shifts requires time:can_search_sites". The gateway sends them
// as one "; "-separated description; older shapes carried a list, so accept
// strings and objects either way.
export const parsePermissionConflicts = (error: any): PermissionRequirementPair[] => {
  const detail = error?.response?.data?.detail;
  const description = detail?.message?.description || detail?.description;
  const raw = Array.isArray(detail)
    ? detail
    : Array.isArray(detail?.pairs)
    ? detail.pairs
    : Array.isArray(detail?.conflicts)
    ? detail.conflicts
    : typeof description === 'string'
    ? description.split(';')
    : [];

  return raw
    .map((item: any): PermissionRequirementPair | null => {
      if (typeof item === 'string') {
        const [permission, requires] = item.split(/\s+requires\s+/i);
        return permission && requires ? { permission: permission.trim(), requires: requires.trim() } : null;
      }
      if (item?.permission && item?.requires) {
        return { permission: item.permission, requires: item.requires };
      }
      return null;
    })
    .filter(Boolean) as PermissionRequirementPair[];
};

/**
 * Returns null when the caller should fall through to handleServerError — same
 * convention as getDocumentErrorNotification.
 */
export const getPermissionErrorNotification = (
  error: any,
  resolveLabel: (systemName: string) => string = systemName => systemName
): AppNotification | null => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  // The backend sent its own structured message — let generateErrorObject render it.
  if (detail?.message?.title) return null;

  if (status === 409) {
    const pairs = parsePermissionConflicts(error);
    return {
      message: "Can't remove those permissions",
      description: pairs.length
        ? pairs
            .map(pair => `${resolveLabel(pair.permission)} needs ${resolveLabel(pair.requires)}`)
            .join('; ')
        : 'Another permission this user keeps still needs one you removed.',
      options: { variant: 'error' },
    };
  }

  if (status === 422) {
    return {
      message: "Those permissions can't be saved",
      // The backend names the offending module, permission or the owner row.
      description: detail?.description || (typeof detail === 'string' ? detail : 'The change was rejected.'),
      options: { variant: 'error' },
    };
  }

  return null;
};

/**
 * Same contract as getPermissionErrorNotification (null ⇒ fall through to
 * handleServerError), with one addition: a role assigned in a module the company
 * has nothing in yet is saved with an empty scope, and if the backend refuses that
 * the raw schema error says nothing a user can act on. Name the modules instead.
 */
export const getSaveErrorNotification = (
  error: any,
  resolveLabel: (systemName: string) => string = systemName => systemName,
  emptyScopeLabels: string[] = []
): AppNotification | null => {
  const status = error?.response?.status;

  if ((status === 400 || status === 422) && emptyScopeLabels.length) {
    return {
      message: "Those roles can't be saved yet",
      description: `${emptyScopeLabels.join(', ')} ${
        emptyScopeLabels.length === 1 ? 'has' : 'have'
      } nothing to be scoped to in this company. Create the first one, then assign the role.`,
      options: { variant: 'error' },
    };
  }

  return getPermissionErrorNotification(error, resolveLabel);
};
